import { HttpErrorResponse } from '@angular/common/http';
import { ErrorHandler, Injectable } from '@angular/core';
import * as Sentry from '@sentry/angular-ivy';
import { AppError } from '@shared/models/app-errors';

const IGNORED_SENTRY_ERROR_PATTERNS = [
  'window.webkit.messagehandlers',
  'java object is gone',
  'setcontactautofillvaluesfrombridge',
  'enabledidusertypeonkeyboardlogging',
  'oauthservice.checksession',
];

function collectSentryEventText(event: any): string {
  const parts: string[] = [];

  if (typeof event?.message === 'string') {
    parts.push(event.message);
  }

  if (typeof event?.logentry?.formatted === 'string') {
    parts.push(event.logentry.formatted);
  }

  const exceptionValues = event?.exception?.values;
  if (Array.isArray(exceptionValues)) {
    exceptionValues.forEach((value: any) => {
      if (typeof value?.type === 'string') {
        parts.push(value.type);
      }

      if (typeof value?.value === 'string') {
        parts.push(value.value);
      }

      const frames = value?.stacktrace?.frames;
      if (Array.isArray(frames)) {
        frames.forEach((frame: any) => {
          if (typeof frame?.function === 'string') {
            parts.push(frame.function);
          }

          if (typeof frame?.filename === 'string') {
            parts.push(frame.filename);
          }
        });
      }
    });
  }

  return parts.join(' ').toLowerCase();
}

export function shouldIgnoreSentryEvent(event: any): boolean {
  const eventText = collectSentryEventText(event);

  return IGNORED_SENTRY_ERROR_PATTERNS.some(pattern => eventText.includes(pattern));
}

@Injectable({
  providedIn: 'root',
})
export class CustomErrorHandler implements ErrorHandler {
  constructor() {}

  handleError(error: unknown): void {
    if (error instanceof AppError && error.isHandled) {
      return;
    }

    if (error instanceof HttpErrorResponse) {
      if (this.shouldIgnoreHttpError(error)) {
        return;
      }

      Sentry.withScope(scope => {
        scope.setTag('error.kind', 'http');
        scope.setContext('http', {
          status: error.status,
          statusText: error.statusText,
          url: error.url ?? '',
          ok: error.ok,
        });
        Sentry.captureException(new Error(error.message || `HTTP ${error.status} ${error.statusText}`));
      });
      return;
    }

    if (error instanceof Error) {
      Sentry.captureException(error);
      return;
    }

    const fallbackMessage =
      typeof error === 'string'
        ? error
        : typeof error === 'object' && error !== null && 'type' in error && typeof error.type === 'string'
          ? error.type
          : 'Unknown application error';

    Sentry.withScope(scope => {
      if (typeof error === 'object' && error !== null) {
        scope.setContext('non_error_exception', {
          serialized: this.serializeForSentry(error),
        });
      }

      Sentry.captureException(new Error(fallbackMessage));
    });
  }

  private serializeForSentry(error: object): string {
    try {
      return JSON.stringify(error);
    } catch {
      return '[unserializable exception]';
    }
  }

  private shouldIgnoreHttpError(error: HttpErrorResponse): boolean {
    return error.status === 0 && (error.url ?? '').includes('/assets/i18n_translations/');
  }
}
